import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  calculateAndSaveGeneric,
  getNonSelectedPlanCover,
  getProposalExpatDetails,
  saveProposalDraftGeneric,
} from './queries';
import { validateProposalPageForm } from './validation';
import {
  convertProposalPageStateToSubmission,
  IProposalDetailsExpatPageSubmissionModel,
} from './submission';
import { toast } from 'react-toastify';
import { isEmpty, isValidNumber } from '@utils/validationUtils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import EnhancedStepper from '@components/stepper/EnhancedStepper';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { useParams } from 'react-router-dom';
import CloseProposalDrawer from '../../close-proposal-drawer/CloseProposalDrawer';
import StaticLayout from '@components/layout/StaticLayout';
import Loader from '@components/loader/Loader';
import { ComissionProposalWidget } from '../../proposal-details-page/custom-widgets/commission/ComissionProposalWidget';
import { getProposalStatus, LookupToList } from './utils';
import PolicyProposalQuoteWidget from '../../proposal-details-page/custom-widgets/PolicyProposalQuoteWidget';
import PolicyCoversRepeaterWidget from '../../proposal-details-page/custom-widgets/PolicyCoversRepeaterWidget';
import PolicyCostChargesWidget from '../../proposal-details-page/custom-widgets/PolicyCostChargesWidget';
import PolicyInsuredWidget from '../../proposal-details-page/custom-widgets/PolicyInsuredWidget';
import {
  getProposalPageFormState,
  IProposalCover,
  IProposalPageFormState,
} from './form';
import {
  convertToDetailsSummary,
  convertToPolicyBankersBlanketBondDetails,
  convertToPolicyPersons,
  convertToPolicyPlanProposalCovers,
  convertToPolicyPropertyDetails,
  convertToProposalCover,
  getPlanCoversLov,
  IProposalDetailsPageResponse,
  IProposalDetailsSummary,
} from './index2';
import PolicyDetailsEntityInfo from '../../proposal-details-page/custom-widgets/PolicyDetailsEntityInfo';
import { PolicyDetailsWidget } from '../../proposal-details-page/custom-widgets/PolicyDetailsWidget';
import PolicyPayerWidget from '../../proposal-details-page/custom-widgets/PolicyPayerWidget';
import PolicyOwnerWidget from '../../proposal-details-page/custom-widgets/PolicyOwnerWidget';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import BankersDetailsRepeaterWidget from '../../proposal-details-page/custom-widgets/BankersDetailsRepeaterWidget';
import PreviewProposalClauseModalShell from '../modals/PreviewProposalClauseModalShell';
import ProposalMarineDetailsWidget from '../../proposal-details-page/custom-widgets/ProposalMarineDetailsWidget';
import ProposalMarineHullDetailsWidget from '../../proposal-details-page/custom-widgets/ProposalMarineHullDetailsWidget';
import ProposalDescriptionOfRiskWidget from '../../proposal-details-page/custom-widgets/ProposalDescriptionOfRiskWidget';
import ProposalPropertyDescriptionDetailsWidget from '../../proposal-details-page/custom-widgets/ProposalPropertyDescriptionDetailsWidget';
import { GenericLines, getGenericLine } from '@utils/helper-utils';
import { IEnhancedMenuItem } from '@components/table/types';
import SimpleActionBar from '@components/menu/SimpleActionBar';
import { useStepperStyles, useTabsLayoutStyles } from '@styles/sharedStyles';

const ProposalGenericPage: React.FC = () => {
  const { id: entityId } = useParams<{ id: string }>();
  const { classes: tabClasses } = useTabsLayoutStyles();
  const { classes: stepperClasses } = useStepperStyles({
    mainContainerRatio: 8,
  });

  const [closeProposalDrawerOpen, setCloseProposalDrawerOpen] =
    useState<boolean>(false);
  const [issueModalOpen, setIssueModalOpen] = useState<boolean>(false);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const [pageState, onPageStateUpdate] = useState<IProposalPageFormState>(
    getProposalPageFormState()
  );
  const [data, setData] = useState<IProposalDetailsSummary>();

  const [coversList, setCoversList] = useState<IProposalCover[]>([]);
  const [planCoversList, setPlanCoversList] = useState<
    Record<string, IProposalCover>
  >({});
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({
    planCovers: {},
  });

  const [getProposalDetailsLazy] = useLazyQuery(getProposalExpatDetails());
  const [getNonSelectedPlanCoverLazy] = useLazyQuery(getNonSelectedPlanCover());
  const [saveAndCalculationAction] = useMutation(calculateAndSaveGeneric());
  const [saveAsDraftAction] = useMutation(saveProposalDraftGeneric());

  useEffect(() => {
    initialize();
  }, []);

  const isActionVisible = [
    '12',
    '3',
    '33',
    '5',
    '9',
    '10',
    '13',
    '21',
    '35',
    '11',
    '15',
    '16',
    '25',
    '27',
    '29',
    '30',
    '31',
    '37',
    '38',
    '39',
    '42',
    '46',
    '2',
    '8',
    '23',
    '26',
  ].includes(data?.LineId?.ExternalCode);

  const genericLine = getGenericLine(data?.LineId?.ExternalCode);

  const ownerIsDifferent: boolean =
    pageState?.values?.policyPersons?.payer?.ownerIsDifferent || false;

  const isClosed =
    data?.Status === 'CLOSED_WON' || data?.Status === 'CLOSED_LOST';

  const disabledPage = isClosed || submitting || loading;

  const getPlanCoverDetails = async (
    planId: string,
    planCoverIds: string[]
  ) => {
    const { data } = await getNonSelectedPlanCoverLazy({
      variables: {
        selectedPlanID: planId,
        selectedPlanCoverIDs: planCoverIds,
      },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });

    return data;
  };

  const initialize = async () => {
    setLoading(true);
    const result: { data: IProposalDetailsPageResponse } =
      await getProposalDetailsLazy({
        variables: { id: entityId },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      });

    const summaryData = convertToDetailsSummary(result.data);

    const initialCovers = convertToPolicyPlanProposalCovers(result.data);
    const initialBankers = convertToPolicyBankersBlanketBondDetails(
      result.data
    );
    const initialPersons = convertToPolicyPersons(result.data);
    const initialPolicyProperties = convertToPolicyPropertyDetails(result.data);

    const planCoverDetails = await getPlanCoverDetails(
      summaryData.PlanID.Id,
      []
    );

    const lists = LookupToList(result.data);
    setLovs({
      currencySymbols: lists.currencySymbols,
      medicalClasses: lists.medicalClasses,
      continuityOptions: lists.continuityOptions,
      matters: lists.matters,
      geoLocations: lists.geoLocations,
      planCovers: getPlanCoversLov(planCoverDetails),
    });

    setPlanCoversList(
      convertToProposalCover(
        planCoverDetails,
        summaryData.PolicyCurrency?.Code,
        summaryData.PricingOption.PlanToPolicyRate,
        summaryData.PolicyEffectiveDate,
        summaryData.PolicyExpiryDate
      )
    );
    setCoversList(initialCovers);

    const newPageState = getProposalPageFormState(
      summaryData,
      initialCovers,
      initialPersons,
      initialBankers,
      initialPolicyProperties
    );
    onPageStateUpdate(newPageState);

    setData(summaryData);
    setLoading(false);
  };

  const renderLeftSection = () => {
    if (data) {
      return <PolicyDetailsEntityInfo data={data as any} />;
    }

    return <></>;
  };

  const onSubmit = async () => {
    const validationResult = validateProposalPageForm(data, pageState.values);
    if (!submitting) {
      setSubmitting(true);
      const newPageState = {
        ...pageState,
        errors: validationResult.errors,
        touched: validationResult.touched,
      };

      onPageStateUpdate(newPageState);
      if (validationResult.isValid) {
        const dataToSubmit = convertProposalPageStateToSubmission(
          data,
          pageState
        );
        const result = await saveAndCalculationAction({
          variables: { ...dataToSubmit },
          errorPolicy: 'all',
        });
        if (isEmpty(result.errors)) {
          toast.success(
            <ToastSuccessMessage>
              {'Proposal successfully saved.'}
            </ToastSuccessMessage>
          );
          initialize();
        } else {
          toast.error(
            <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
          );
        }
      } else {
        toast.error(<ToastErrorMessage>Incomplete Form</ToastErrorMessage>);
      }
      setSubmitting(false);
    }
  };

  const onSaveDraft = async () => {
    try {
      if (!submitting) {
        setSubmitting(true);
        const dataToSubmit = convertProposalPageStateToSubmission(
          data,
          pageState
        ) as Partial<IProposalDetailsExpatPageSubmissionModel>;

        if (isEmpty(dataToSubmit.policyPersonInput[0]?.personId)) {
          dataToSubmit.policyPersonInput = [];
        }

        const result = await saveAsDraftAction({
          variables: { ...dataToSubmit },
          errorPolicy: 'all',
        });

        if (isEmpty(result.errors)) {
          toast.success(
            <ToastSuccessMessage>
              {'Proposal successfully saved.'}
            </ToastSuccessMessage>
          );

          initialize();
        } else {
          toast.error(
            <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
          );
        }
      }
      setSubmitting(false);
    } catch {
      setSubmitting(false);
    }
  };

  const renderActions = () => {
    const actions: IEnhancedMenuItem[] = [
      {
        name: 'saveDraft',
        title: 'Save Draft',
        onClick: () => {
          onSaveDraft();
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden:
          isClosed || data?.Status?.toLowerCase() !== 'new' || !isActionVisible,
      },
      {
        name: 'calculateAndSave',
        title: 'Calculate & Save',
        onClick: () => {
          onSubmit();
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden:
          isClosed ||
          !isValidNumber(data?.LineId?.ExternalCode) ||
          !isActionVisible,
      },
      {
        name: 'issuePolicy',
        title: 'Issue Policy',
        onClick: () => {
          setIssueModalOpen(true);
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden:
          isClosed ||
          !(
            data?.Status?.toLowerCase() === 'in_progress' &&
            isValidNumber(data?.LineId?.ExternalCode)
          ) ||
          !isActionVisible,
      },
      {
        name: 'closeProposal',
        title: 'Close Proposal',
        onClick: () => {
          setCloseProposalDrawerOpen(true);
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden: isClosed,
      },
    ];

    return <SimpleActionBar items={actions} />;
  };

  const renderMainChildren = () => {
    if (data) {
      const isProposalWon = data.Status?.toUpperCase() === 'CLOSED_WON';
      const isProposalUserNew = data.Status?.toLowerCase() === 'new';
      const steps = isProposalUserNew
        ? ['New', 'In Progress', 'Closed Won']
        : ['New', 'In Progress', isProposalWon ? 'Closed Won' : 'Closed Lost'];

      return (
        <section>
          <div className={stepperClasses.stepperContainer}>
            <div className={stepperClasses.stepperWrapper}>
              <EnhancedStepper
                activeStep={getProposalStatus(steps, data.Status)}
                steps={steps}
                buttonTitle="Calculate & Save"
                buttonOnClick={() => undefined}
              />
            </div>
            <div className={stepperClasses.actionsWrapper}>
              {renderActions()}
            </div>
          </div>
          <div className={tabClasses.tabSplitBody}>
            <div className={tabClasses.tabMainBody}>
              <PolicyDetailsWidget data={data as any} />

              {genericLine === GenericLines.MARINE && (
                <ProposalMarineDetailsWidget
                  pageState={pageState}
                  onPageStateUpdate={onPageStateUpdate}
                  data={data}
                  disabledForm={disabledPage}
                  lovs={lovs}
                />
              )}

              <PolicyCoversRepeaterWidget
                defaultPlanCoversList={planCoversList}
                pageState={pageState as any}
                disabledForm={disabledPage}
                status={data.Status}
                data={data as any}
                allowPlanCoverEdits={data.BusinessUserID.AllowPlanCoverEdits}
                onPageStateUpdate={onPageStateUpdate as any}
                lovs={lovs}
              />
              {['2', '8', '23', '26'].includes(data?.LineId?.ExternalCode) && (
                <ProposalPropertyDescriptionDetailsWidget
                  pageState={pageState}
                  onPageStateUpdate={onPageStateUpdate}
                  disabledForm={disabledPage}
                  lovs={lovs}
                  currencySymbol={data.PolicyCurrency.Symbol}
                />
              )}
              <PolicyCostChargesWidget
                pageState={pageState as any}
                disabledForm={disabledPage}
                status={data.Status}
                line={data?.LineId.ExternalCode?.toString() || ''}
                editableLine={data.BusinessUserID?.EditableLineChargesIDs?.includes(
                  data?.LineId.Id
                )}
                onPageStateUpdate={onPageStateUpdate as any}
                currencySymbol={data.PolicyCurrency.Symbol}
              />
              {[
                '5',
                '9',
                '10',
                '13',
                '21',
                '35',
                '11',
                '15',
                '16',
                '25',
                '27',
                '29',
                '30',
                '31',
                '37',
                '38',
                '39',
                '42',
                '46',
              ].includes(data?.LineId?.ExternalCode) && (
                <ProposalDescriptionOfRiskWidget
                  pageState={pageState}
                  onPageStateUpdate={onPageStateUpdate}
                  status={data.Status}
                  disabledForm={disabledPage}
                  lovs={lovs}
                  lineExternalCode={data?.LineId?.ExternalCode}
                />
              )}
              <WidgetPaper>
                <WidgetSection title="Payer & Owner">
                  <PolicyPayerWidget
                    pageState={pageState as any}
                    onPageStateUpdate={onPageStateUpdate as any}
                    disabledForm={disabledPage}
                    data={data as any}
                  />
                  {ownerIsDifferent && (
                    <PolicyOwnerWidget
                      pageState={pageState as any}
                      onPageStateUpdate={onPageStateUpdate as any}
                      disabledForm={disabledPage}
                      data={data as any}
                    />
                  )}
                </WidgetSection>
              </WidgetPaper>

              <PolicyInsuredWidget
                pageState={pageState as any}
                onPageStateUpdate={onPageStateUpdate as any}
                disabledForm={disabledPage}
                maxNumber={data.PlanID.MaxNbInsured}
                data={data as any}
              />

              {genericLine === GenericLines.BBB && (
                <BankersDetailsRepeaterWidget
                  pageState={pageState}
                  onPageStateUpdate={onPageStateUpdate}
                  disabledForm={disabledPage}
                />
              )}

              {genericLine === GenericLines.MARINE_HULL && (
                <ProposalMarineHullDetailsWidget
                  pageState={pageState}
                  onPageStateUpdate={onPageStateUpdate}
                  disabledForm={disabledPage}
                />
              )}
              {closeProposalDrawerOpen && (
                <CloseProposalDrawer
                  open={closeProposalDrawerOpen}
                  onClose={() => {
                    setCloseProposalDrawerOpen(false);
                  }}
                  onSuccess={() => {
                    initialize();
                    setCloseProposalDrawerOpen(false);
                  }}
                  id={entityId}
                />
              )}
            </div>
            <div className={tabClasses.tabRightSection}>
              <PolicyProposalQuoteWidget
                data={data as any}
                covers={coversList}
              />
              {data?.Status && data?.Status?.toLowerCase() !== 'new' && (
                <ComissionProposalWidget
                  data={data as any}
                  onUpdateSuccess={() => {
                    initialize();
                  }}
                />
              )}
            </div>
          </div>
        </section>
      );
    }

    return <></>;
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      <StaticLayout
        name={'Proposal Details'}
        leftChildren={renderLeftSection()}
        mainChildren={renderMainChildren()}
      />
      {issueModalOpen && (
        <PreviewProposalClauseModalShell
          data={data}
          onSuccess={() => {
            setIssueModalOpen(false);
            initialize();
          }}
          onClose={() => setIssueModalOpen(false)}
        />
      )}
    </>
  );
};

export default ProposalGenericPage;
